import { Gift } from "lucide-react";

const Section = ({ emoji, title, subtitle, children }) => (
  <div className="mt-8 first:mt-0">
    <h4 className="font-heading font-bold text-lg sm:text-xl text-white flex items-center gap-2.5">
      <span className="text-xl leading-none">{emoji}</span>
      <span className="text-gradient-accent">{title}</span>
    </h4>
    {subtitle && (
      <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-cyan-400/80">
        {subtitle}
      </p>
    )}
    <div className="mt-3 space-y-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const networks = [
  { from: "Base", to: "Base addresses" },
  { from: "Monad", to: "Monad addresses" },
  { from: "Robinhood", to: "Robinhood addresses" },
  { from: "Ethereum", to: "Ethereum addresses" },
];

const sources = [
  "Token holders",
  "Recent token activity",
  "Addresses interacting with a selected contract",
  "Liquidity participants",
  "Stakers",
  "Other eligible on-chain addresses",
];

export const AirdropDetail = () => {
  return (
    <div data-testid="airdrop-detail-content">
      <Section emoji="🎁" title="Autonomous Airdropper" subtitle="Airdrops Without the Spreadsheet">
        <p>
          Autono Dex makes token airdrops simpler by allowing you to automatically populate
          recipient wallets directly from blockchain data.
        </p>
        <div className="grid gap-2 pt-1">
          {[
            "No manually entering hundreds of wallet addresses.",
            "No required CSV uploads.",
            "No copying and pasting addresses one by one.",
          ].map((x) => (
            <div
              key={x}
              className="rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-xs text-slate-200"
            >
              {x}
            </div>
          ))}
        </div>
        <p>
          Simply select your EVM network, choose your token, and select how many recipients you want
          to include.
        </p>
      </Section>

      <Section emoji="⚡" title="Select. Populate. Airdrop.">
        <p>Choose the number of recipients:</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {["50", "100", "500", "1,000", "Custom"].map((n) => (
            <span
              key={n}
              className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 font-mono text-xs sm:text-sm text-cyan-300"
            >
              {n}
            </span>
          ))}
        </div>
        <p>
          Autono Dex then retrieves real EVM wallet addresses from the selected network's blockchain
          data and automatically populates your recipient list. For example:
        </p>
        <div className="rounded-xl border border-white/10 bg-black/40 p-4 font-mono text-xs sm:text-sm space-y-1.5">
          <p className="text-slate-400">Network: <span className="text-white">Base</span></p>
          <p className="text-slate-400">Recipients: <span className="text-white">500</span></p>
          <p className="text-slate-400">Addresses found: <span className="text-cyan-300">500</span></p>
        </div>
        <p className="text-slate-400">
          The selected addresses are loaded directly into the airdropper, ready for distribution.
        </p>
      </Section>

      <Section emoji="🌐" title="Network-Aware">
        <p>Your recipient search follows the network you've selected.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          {networks.map((n) => (
            <div
              key={n.from}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 font-mono text-xs text-slate-200"
            >
              <span className="text-white font-semibold">{n.from}</span>
              <span className="text-cyan-400">→</span>
              <span className="text-slate-400">{n.to}</span>
            </div>
          ))}
        </div>
        <p className="text-slate-400">
          As Autono Dex expands to additional EVM networks, the Airdropper can expand with them.
        </p>
      </Section>

      <Section emoji="🧠" title="Choose Your Recipient Source">
        <p>
          Autono Dex can be designed to retrieve eligible addresses based on blockchain activity and
          project criteria, such as:
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {sources.map((s) => (
            <span
              key={s}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {s}
            </span>
          ))}
        </div>
        <p className="text-slate-400">
          This gives project creators flexibility in deciding who should receive the airdrop.
        </p>
      </Section>

      <Section emoji="🔐" title="Real Addresses. On-Chain Data.">
        <p>Autono Dex does not generate artificial wallet addresses.</p>
        <p>
          Recipient addresses are retrieved from blockchain data associated with the selected
          network and filtered according to the airdrop's chosen criteria.
        </p>
        <p className="text-slate-400">
          Before sending, creators can review the generated recipient list and verify the
          distribution parameters.
        </p>
      </Section>

      <Section emoji="🚀" title="Built for Scale">
        <p>
          Whether you're distributing tokens to 50 wallets or thousands of recipients, Autono Dex is
          designed to eliminate the tedious work of manually assembling an airdrop list.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {[
            "Select your network",
            "Choose your recipients",
            "Set your distribution",
            "Approve the transaction",
          ].map((x) => (
            <span
              key={x}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {x}
            </span>
          ))}
        </div>
      </Section>

      <div className="mt-8 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-purple-600/5 p-5 flex items-start gap-3">
        <Gift className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-heading font-bold text-white">Autono Dex Airdropper</p>
          <p className="mt-1 text-sm text-slate-300">
            Less manual work. More automation. Fully on-chain.
          </p>
        </div>
      </div>
    </div>
  );
};
